import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import { ArrowGlyph, Stage } from '@/components/agents/primitives';

import { useMusicT, useSongIdea } from './kit';
import type { MusicLandingPage } from './types';

/** Back to the song box, with an idea in it if the box is still empty. */
export default function FinalCta({ page }: { page: MusicLandingPage }) {
  const { t } = useMusicT();
  const { idea, lyrics, fillIdea, boxRef } = useSongIdea();

  const toBox = () => {
    if (page.mode === 'lyrics' || idea.trim() || lyrics.trim()) {
      const box = boxRef.current;
      box?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => box?.focus({ preventScroll: true }), 400);
      return;
    }
    const first = page.ideas[0];
    fillIdea(first ? t(first.text) : t(page.placeholder), first?.instrumental);
  };

  return (
    <section className='pb-20 pt-6 sm:pb-28'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='indigo'
          halftoneAt='100% 100%'
          className='px-6 py-16 text-center sm:px-12 sm:py-20'
        >
          <h2 className='mx-auto max-w-3xl text-[2.25rem] font-semibold leading-[1.08] tracking-[-0.03em] text-white sm:text-5xl'>
            {page.mode === 'lyrics'
              ? t('Your lyrics deserve to be heard.')
              : t('Your next song starts with one sentence.')}
          </h2>
          <p className='mx-auto mt-5 max-w-xl text-lg leading-8 text-white/80'>
            {t(
              'Free to try: one song a month in two versions, no credit card needed.',
            )}
          </p>
          <div className='mt-9 flex flex-wrap items-center justify-center gap-3'>
            <button
              type='button'
              onClick={toBox}
              className='inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-primary-700 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.35)] transition hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
            >
              {page.mode === 'lyrics'
                ? t('Add your lyrics')
                : t('Create a song')}
              <ArrowGlyph />
            </button>
            <Link
              href='/pricing'
              className={clsxm(
                'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold text-white ring-1 ring-white/40 transition hover:ring-white/70',
              )}
            >
              {t('See plans')}
            </Link>
          </div>
        </Stage>
      </div>
    </section>
  );
}
